/* Genre-based popular movies grid powered by IMDb API. */

import { useState, useCallback } from "react";
import { useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Loader2, Film, Star, Clock } from "lucide-react";
import type { PopularMovie } from "@/types/movieDiscover";

const GENRE_OPTIONS = [
  { value: "action", label: "Action" },
  { value: "comedy", label: "Comedy" },
  { value: "drama", label: "Drama" },
  { value: "scifi", label: "Sci-Fi" },
  { value: "thriller", label: "Thriller" },
  { value: "horror", label: "Horror" },
  { value: "romance", label: "Romance" },
  { value: "animation", label: "Animation" },
  { value: "documentary", label: "Documentary" },
  { value: "crime", label: "Crime" },
  { value: "mystery", label: "Mystery" },
  { value: "fantasy", label: "Fantasy" },
] as const;

interface MovieBrowsePopularProps {
  onSelectMovie: (movie: PopularMovie) => void;
}

export function MovieBrowsePopular({ onSelectMovie }: MovieBrowsePopularProps) {
  const [movies, setMovies] = useState<PopularMovie[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeGenre, setActiveGenre] = useState<string | null>(null);

  const fetchPopular = useAction(api.movieDiscover.fetchPopularMovies);

  const loadGenre = useCallback(
    async (genre: string) => {
      setActiveGenre(genre);
      setIsLoading(true);
      try {
        const data = await fetchPopular({ genre });
        setMovies(data);
      } catch {
        setMovies([]);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchPopular],
  );

  function handleGenreClick(genre: string) {
    if (activeGenre === genre) return;
    loadGenre(genre);
  }

  return (
    <div className="space-y-3">
      {/* Genre pill filter bar */}
      <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1">
        {GENRE_OPTIONS.map((g) => (
          <button
            key={g.value}
            type="button"
            className={`shrink-0 rounded-full px-2.5 py-1 text-xs transition-colors ${
              activeGenre === g.value
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-muted/80"
            }`}
            onClick={() => handleGenreClick(g.value)}
          >
            {g.label}
          </button>
        ))}
      </div>

      {/* Prompt to select genre */}
      {!activeGenre && !isLoading && (
        <p className="text-xs text-muted-foreground text-center py-3">
          Select a genre to browse popular movies.
        </p>
      )}

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center gap-2 py-4 justify-center">
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            Loading popular movies...
          </span>
        </div>
      )}

      {/* Movies grid */}
      {!isLoading && movies.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
          {movies.map((movie) => (
            <button
              key={movie.imdbId}
              type="button"
              className="flex flex-col items-center gap-1 p-2 rounded-md border border-border cursor-pointer hover:bg-muted/50 transition-colors"
              onClick={() => onSelectMovie(movie)}
            >
              {movie.poster ? (
                <img
                  src={movie.poster}
                  alt={movie.title}
                  className="w-16 h-24 rounded-sm object-cover bg-muted"
                />
              ) : (
                <div className="w-16 h-24 rounded-sm bg-muted flex items-center justify-center">
                  <Film className="size-5 text-muted-foreground/50" />
                </div>
              )}
              <span className="text-xs font-medium leading-tight text-center line-clamp-2 w-full">
                {movie.title}
              </span>
              <div className="flex items-center gap-1 text-[10px] text-muted-foreground flex-wrap justify-center">
                <span>{movie.year}</span>
                {movie.runtimeMinutes && (
                  <>
                    <span>·</span>
                    <Clock className="size-2.5" />
                    <span>{movie.runtimeMinutes}m</span>
                  </>
                )}
                {movie.rating > 0 && (
                  <>
                    <span>·</span>
                    <Star className="size-2.5 fill-current text-yellow-500" />
                    <span>{movie.rating}</span>
                  </>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && activeGenre && movies.length === 0 && (
        <p className="text-xs text-muted-foreground text-center py-3">
          No results found for this genre.
        </p>
      )}
    </div>
  );
}
